// The tutor-side English diagnostics report: its four endpoints, and the adapter that lets the
// existing BMD report components render them.
//
// WHY AN ADAPTER RATHER THAN ENGLISH COMPONENTS. The report dashboard is roughly 7,000 lines of
// presentation - KPI strip, radar, mastery table, attempt history, review modal - and none of it
// is arithmetic. Only the QUERIES underneath were maths-specific, which is why the backend has
// an English twin and the frontend does not. Mapping English data into the props those
// components already take keeps one dashboard for a tutor who switches subject with a dropdown,
// and one place to fix a rendering bug.
//
// The vocabulary maps cleanly because the two trees have the same shape one rung down: what
// maths calls a STRAND is an English CLUSTER, and both sit above topics. The server already
// returns English clusters under `strand_*`, so the mapping here is mostly pass-through; the
// English-only fields it adds are documented where they appear.

// Uses the shared `apiClient`, not raw axios: it injects the Bearer token and handles the
// expired-token redirect, the same as the maths report service.
import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type {
  BmdReportKpi,
  BmdReportStudent,
  BmdReportYearLevel,
  BmdReportSession,
  BmdTopicStrength,
  BmdStrandStrength,
  BmdAttemptHistoryRow,
  BmdAttemptDetail,
  BmdMasteryCluster,
  BmdMasteryStats,
  BmdReviewQuestion,
  GetStudentReportResponse,
  GetTopicAttemptDetailResponse,
} from "../../bmd/types/bmd-report.types"

const ENDPOINTS = {
  getStudentReport: "engDiag/getStudentReport",
  getTopicAttemptDetail: "engDiag/getTopicAttemptDetail",
  getStudentSkillsGap: "engDiag/getStudentSkillsGap",
  getStudentPrereqGraph: "engDiag/getStudentPrereqGraph",
} as const

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

/**
 * The English KPI strip.
 *
 * Differs from the maths one in three ways, each because English data differs:
 *  - `answers` is separate from `questions`. An English question can be marked in several parts
 *    (a reading comprehension is graded per sub-question), so 41 questions can yield 45 answers.
 *    Accuracy is over answers, which is what was actually judged.
 *  - `awaiting_marking` counts answers sitting with a tutor. They are excluded from accuracy
 *    rather than counted wrong, because an unmarked answer says nothing yet.
 *  - no `incorrect_fm_triggered`. English has no fundamental-misconception retry, so the key is
 *    absent rather than zero.
 *  - the `sb_*` block is wider. Maths sends two numbers; English adds what has been assigned,
 *    what is waiting on a tutor inside those Skill Builders, and the failed skills the question
 *    bank cannot fill yet. Optional on the way in, so an older backend hides the card instead
 *    of reading "0 queued".
 */
export interface EngReportKpi {
  questions: number
  questions_attempted: number
  questions_not_attempted: number
  topics: number
  answers: number
  correct: number
  incorrect_total: number
  accuracy_pct: number
  incorrect_chain_triggered: number
  awaiting_marking: number
  sb_queued?: number
  sb_assigned?: number
  sb_completed?: number
  sb_awaiting_marking?: number
  sb_no_questions?: number
}

export interface EngReportClusterStrength {
  strand_id: number
  strand_name: string
  order: number
  answered: number
  correct: number
  attempted: boolean
  score_pct: number
  /**
   * Both scopes, on every axis. Required here, unlike the maths type: the English report sends
   * them precisely because its two strength lists used to count different things - the per-topic
   * one the base test, this one base plus the prerequisite test - with nothing saying which.
   * The flat figures above are the base test.
   */
  base: BmdMasteryStats
  overall: BmdMasteryStats
}

export interface EngReportAttemptRow {
  pk_session_topic_key: string
  fk_diagnostics_session_key: string
  topic_name: string
  strand_id: number | null
  strand_name: string
  year_level_id: number | null
  level_name: string | null
  topic_status: string
  /** True for a block belonging to the prerequisite test rather than the base one. */
  is_prereq: boolean
  attempted_on: string | null
  correct_count: number
  total_count: number
  awaiting_marking: number
  has_unresolved_flag: boolean
}

export interface GetEngStudentReportResponse {
  status: number
  student: BmdReportStudent
  available_year_levels: BmdReportYearLevel[]
  selected_year_level: BmdReportYearLevel | null
  kpi: EngReportKpi | null
  kpi_overall: EngReportKpi | null
  topic_strength: BmdTopicStrength[]
  strand_strength: EngReportClusterStrength[]
  /**
   * Same shape as the maths tree, with `base` and `overall` on every node. A subtopic carries the
   * year of its SKILLS, not of the test: a Year 2 test can expose a Reception weakness.
   */
  mastery_tree: BmdMasteryCluster[]
  available_sessions: BmdReportSession[]
  selected_session_key: string | null
  attempts: EngReportAttemptRow[]
  /** Always empty: English raises no intervention flags. The panel collapses. */
  intervention_flags: never[]
  error?: string
}

export interface EngReportAnswer {
  pk_attempt_key: string
  /** The skill this part tested, which is how a reading comprehension credits each one. */
  skill_id: number | null
  /** Which PART of the question. Empty string when the answer is the whole question. */
  target_ref: string
  response: unknown
  is_correct: boolean | null
  misconception_type: string | null
  /** The readable name of that misconception. Null when the wrong answer carries no tag. */
  misconception_label: string | null
  /** The author's note on the wrong answer that matched - why this mistake gets made. */
  wrong_answer_note: string | null
  /** The skill this part tested; differs per part on a reading comprehension. */
  skill_name: string | null
  marking_status: string | null
  skip_reason: string | null
  duration_seconds: number | null
  answered_on: string | null
}

/**
 * One node of the prerequisite tree under a question's skill, as the curriculum team mapped it.
 *
 * Same shape as the maths `BmdPrereqChainStep`, because the Chain Analysis tab renders both.
 * `children` is ordered by `prereq_order`, so children[0] is the line the collapsed view opens
 * on. `student_result` is null for a skill the student has never been asked - and only then
 * should the UI say "not tested".
 */
export interface EngPrereqChainStep {
  level: number
  skill_id: number
  skill_name: string | null
  year_level_name: string | null
  student_result: "correct" | "wrong" | null
  student_misconception: string | null
  /** Expanded elsewhere in the same tree, so genuinely required but not repeated here. */
  repeat?: boolean
  children: EngPrereqChainStep[]
}

export interface EngReportReviewQuestion {
  pk_session_question_key: string
  phase: string
  question_order: number
  fk_parent_session_question_key: string | null
  /** The skill the question is filed under. Null where only its parts carry one. */
  skill_id: number | null
  /** What that skill rests on. Populated for BASE questions only; a PREREQ question is already
   * inside a chain, so it carries an empty one. */
  theoretical_prereq_chain: EngPrereqChainStep[]
  skill_name: string | null
  sub_topic_name: string | null
  year_level_name: string | null
  is_manually_marked: boolean
  snapshot: {
    question_id: string
    type: string
    instruction: string | null
    content: string
    options: Record<string, unknown>
  }
  answers: EngReportAnswer[]
}

export interface GetEngTopicAttemptDetailResponse {
  status: number
  topic: {
    pk_session_topic_key: string
    topic_name: string
    cluster_name: string
    topic_status: string
    year_level_name: string
    is_prereq: boolean
    attempted_on: string | null
  }
  questions: EngReportReviewQuestion[]
  totals: {
    correct: number
    total: number
    accuracy_pct: number
    awaiting_marking: number
  }
  error?: string
}

export interface EngSkillGapRow {
  pk_attempt_key: string
  pk_session_question_key: string
  phase: string
  target_ref: string
  skill_id: number | null
  skill_name: string | null
  sub_topic_name: string | null
  topic_name: string | null
  strand_id: number | null
  strand_name: string | null
  year_level_name: string | null
  is_correct: boolean
  misconception_type: string | null
  misconception_label: string | null
  /**
   * Where this mistake sends the student. Type B (Foundational Gaps) routes to the prerequisite
   * chain; Types A and C route to a Skill Builder. Null when the wrong answer carries no
   * misconception tag. Worth grouping by: it is the action a tutor decides from, rather than
   * the label.
   */
  routes_to: "prerequisite" | "skill_builder" | null
  /**
   * The marked parts folded into this row, and how many were right. An image_sort marked per
   * item produces one row carrying all of them. The row is correct only when every part was.
   */
  parts_total: number
  parts_correct: number
}

export interface GetEngSkillsGapResponse {
  status: number
  skills_with_gaps: EngSkillGapRow[]
  counts: {
    answers: number
    /** Rows the table lists: the parts of one question, per skill, folded into one. */
    rows: number
    /** Distinct wrong skills that have prerequisites - the chains the test will actually run. */
    chains_triggered: number
    correct: number
    incorrect: number
    distinct_skills: number
    distinct_skills_wrong: number
    by_misconception: Record<string, number>
    routes_to_prerequisite: number
    routes_to_skill_builder: number
  }
  error?: string
}

export interface EngPrereqGraphNode {
  skill_id: number
  skill_name: string
  skill_order: number
  topic_id: number
  topic_name: string
  sub_strand_id: number | null
  sub_strand_name: string
  strand_id: number | null
  strand_name: string
  year_level_id: number | null
  year_level_name: string
  year_level_order: number
  attempt_status: "correct" | "wrong" | "not_attempted"
}

export interface GetEngPrereqGraphResponse {
  status: number
  strands: { strand_id: number; strand_name: string; order: number }[]
  nodes: EngPrereqGraphNode[]
  edges: { from_skill_id: number; to_skill_id: number; is_cross_strand: boolean }[]
  summary: { skills: number; dependencies: number; cross_strand: number }
  error?: string
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

async function post<T>(endpoint: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const response = await apiClient.post<T>(BASE_URL + endpoint, body, { signal })
  return response.data
}

export function getEngStudentReport(
  req: {
    student_key: string
    year_level_id?: number
    diagnostics_session_key?: string | null
  },
  signal?: AbortSignal,
): Promise<GetEngStudentReportResponse> {
  return post(ENDPOINTS.getStudentReport, req, signal)
}

export function getEngTopicAttemptDetail(
  req: { session_topic_key: string },
  signal?: AbortSignal,
): Promise<GetEngTopicAttemptDetailResponse> {
  return post(ENDPOINTS.getTopicAttemptDetail, req, signal)
}

export function getEngStudentSkillsGap(
  req: {
    student_key: string
    year_level_id: number
    diagnostics_session_key?: string | null
    scope?: "base" | "overall"
  },
  signal?: AbortSignal,
): Promise<GetEngSkillsGapResponse> {
  return post(ENDPOINTS.getStudentSkillsGap, req, signal)
}

export function getEngStudentPrereqGraph(
  req: {
    student_key: string
    strand_id?: number | null
    year_level_id?: number | null
    /** Scopes the node colours to one diagnostic. Null means the whole year level. */
    diagnostics_session_key?: string | null
  },
  signal?: AbortSignal,
): Promise<GetEngPrereqGraphResponse> {
  return post(ENDPOINTS.getStudentPrereqGraph, req, signal)
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

/**
 * The English report in the shape the BMD dashboard components take.
 *
 * Every number carried across is the English one; nothing is invented. The maths-only fields the
 * components expect are filled with a value that makes the card HIDE rather than show a false
 * zero, and they are listed explicitly below so a reader can see exactly what English does not
 * have. When English skill builders land, delete the corresponding line.
 */
export function toBmdReportShape(
  eng: GetEngStudentReportResponse,
): GetStudentReportResponse {
  const kpi = (k: EngReportKpi | null): BmdReportKpi | null =>
    k === null
      ? null
      : {
          questions: k.questions,
          questions_attempted: k.questions_attempted,
          questions_not_attempted: k.questions_not_attempted,
          topics: k.topics,
          // Both of these are English-only on the maths type, and both have to travel WITH the
          // kpi object rather than beside it: the strip has its own base/overall selector, and a
          // count passed separately would keep showing the overall figure next to base numbers.
          answers: k.answers,
          awaiting_marking: k.awaiting_marking,
          correct: k.correct,
          accuracy_pct: k.accuracy_pct,
          incorrect_total: k.incorrect_total,
          incorrect_chain_triggered: k.incorrect_chain_triggered,
          // English has no fundamental-misconception retry. Zero is the only value the maths
          // type allows, and the figure is not drawn.
          incorrect_fm_triggered: 0,
          // Carried straight across, undefined included: the strip draws the Skill Builders
          // card when the counts arrive, so passing 0 here would put a card on screen saying
          // no remediation is owed for a response that never counted any.
          sb_queued: k.sb_queued,
          sb_assigned: k.sb_assigned,
          sb_completed: k.sb_completed,
          sb_awaiting_marking: k.sb_awaiting_marking,
          sb_no_questions: k.sb_no_questions,
        }

  return {
    student: eng.student,
    available_year_levels: eng.available_year_levels,
    selected_year_level: eng.selected_year_level,
    kpi: kpi(eng.kpi),
    kpi_overall: kpi(eng.kpi_overall),
    topic_strength: eng.topic_strength,
    // No cast. An `as unknown as` here is what let the mastery tree ship in the wrong shape and
    // crash the radar at runtime - the compiler had been told to stop looking at exactly the
    // field that was missing. If these two types ever drift, this line must fail to build.
    strand_strength: eng.strand_strength,
    mastery_tree: eng.mastery_tree,
    available_sessions: eng.available_sessions,
    selected_session_key: eng.selected_session_key,
    attempts: eng.attempts.map(
      (a): BmdAttemptHistoryRow => ({
        pk_session_topic_key: a.pk_session_topic_key,
        fk_diagnostics_session_key: a.fk_diagnostics_session_key,
        topic_name: a.topic_name,
        strand_id: a.strand_id,
        strand_name: a.strand_name,
        year_level_id: a.year_level_id ?? 0,
        level_name: a.level_name ?? "",
        attempted_on: a.attempted_on ?? "",
        correct_count: a.correct_count,
        total_count: a.total_count,
        topic_status: a.topic_status,
        // The score counts settled answers only, so the row has to be able to say what is
        // missing from it rather than quietly showing a smaller denominator.
        awaiting_marking: a.awaiting_marking,
        // A block the chain generated, not one a tutor assigned.
        is_prereq: a.is_prereq,
        has_unresolved_flag: false,
      }),
    ),
    // English raises no intervention flags, so the panel renders nothing at all.
    intervention_flags: [],
  }
}

/**
 * The English fetchers, in the shapes the shared tabs take. Handed to `ReportFetchersProvider`
 * by the report page when the subject is English.
 */
/**
 * An English question's single verdict, folded from its parts.
 *
 * The Chain Analysis tab asks one question of each BASE question: did the student get it wrong?
 * English marks per part, so "wrong" means ANY settled part was wrong - which is the same rule
 * the prerequisite runtime applies when it decides a chain is owed. All parts right is right;
 * nothing settled yet (every part skipped, or waiting on a tutor) is neither, and the tab skips
 * those rather than reporting them as passes.
 */
function foldVerdict(question: EngReportReviewQuestion): BmdAttemptDetail | null {
  const settled = question.answers.filter(
    (a) => !a.skip_reason && a.marking_status !== "PENDING" && a.is_correct !== null,
  )
  if (question.answers.length === 0) return null
  const wrong = settled.find((a) => a.is_correct === false)
  const answeredOn = question.answers
    .map((a) => a.answered_on)
    .filter((value): value is string => !!value)
    .sort()
    .pop()
  return {
    pk_question_attempt_key: question.answers[0].pk_attempt_key,
    // Maths records one chosen answer id per question. English responses are a JSON object
    // keyed by part, shown in full by its own review modal, so there is nothing to put here.
    chosen_answer_id: null,
    is_correct: settled.length === 0 ? null : !wrong,
    misconception_type: wrong?.misconception_type ?? null,
    // Neither is collected in the English flow: no "explain your reasoning" box and no
    // confidence scale. Null rather than a default, so the modal omits the panel.
    answer_reason: null,
    answer_confident: null,
    duration_seconds: question.answers.find((a) => a.duration_seconds != null)?.duration_seconds ?? null,
    skip_reason: question.answers.every((a) => a.skip_reason)
      ? question.answers[0].skip_reason
      : null,
    answered_on: answeredOn ?? null,
  }
}

export const ENG_REPORT_FETCHERS = {
  /**
   * One topic block in the shape the Chain Analysis tab reads.
   *
   * That tab wants, per BASE question: whether it was wrong, its skill, its text, and the
   * prerequisite tree under it. All four exist in the English payload - the verdict has to be
   * folded from the parts, and the question text is the authored instruction, falling back to
   * the content for a question whose prompt lives there.
   *
   * The English REVIEW MODAL does not come through here: it renders the parts individually and
   * reads the English response directly. See EngDiagAttemptReviewModal.
   */
  getStudentTopicAttemptDetail: async (
    req: { session_topic_key: string },
    signal?: AbortSignal,
  ): Promise<GetTopicAttemptDetailResponse> => {
    const eng = await getEngTopicAttemptDetail(req, signal)
    // The envelope reports "no access" and "topic not found" in the BODY with status 0, never as
    // a rejected request. Throwing here is what the Chain Analysis tab's allSettled expects, so
    // one unreadable topic is named and the others still render.
    if (eng.status !== 1 || !eng.topic) {
      throw new Error(eng.error || "Couldn't load this topic")
    }
    return {
      topic: {
        pk_session_topic_key: eng.topic.pk_session_topic_key,
        topic_name: eng.topic.topic_name,
        topic_status: eng.topic.topic_status,
        year_level_name: eng.topic.year_level_name,
        attempted_on: eng.topic.attempted_on ?? "",
      },
      questions: eng.questions.map(
        (question): BmdReviewQuestion => ({
          pk_session_question_key: question.pk_session_question_key,
          phase: question.phase,
          question_order: question.question_order,
          fk_parent_session_question_key: question.fk_parent_session_question_key,
          skill_name: question.skill_name,
          sub_topic_name: question.sub_topic_name,
          theoretical_prereq_chain: question.theoretical_prereq_chain ?? [],
          snapshot: {
            // Maths keys its snapshot by numeric diagnostic id and lists the answer options.
            // English question ids are strings and its options are a per-type payload the
            // shared tab never reads, so only the text crosses over.
            subject_id: 1,
            phase: question.phase,
            diagnostic_id: 0,
            diagnostic_text: question.snapshot.instruction || question.snapshot.content || "",
            answers: [],
          },
          attempt: foldVerdict(question),
        }),
      ),
      totals: {
        correct: eng.totals.correct,
        total: eng.totals.total,
        accuracy_pct: eng.totals.accuracy_pct,
      },
    }
  },

  getStudentSkillsGap: async (
    req: {
      student_key: string
      year_level_id: number
      diagnostics_session_key?: string | null
      scope?: "base" | "overall"
    },
    signal?: AbortSignal,
  ) => {
    const eng = await getEngStudentSkillsGap(req, signal)
    const wrong = eng.skills_with_gaps.filter((row) => !row.is_correct)
    return {
      skills_with_gaps: eng.skills_with_gaps.map((row) => ({
        pk_session_question_key: row.pk_session_question_key,
        skill_name: row.skill_name,
        topic_name: row.topic_name,
        sub_topic_name: row.sub_topic_name,
        year_level_name: row.year_level_name,
        misconception_type: row.misconception_type,
        // Carried so the table can print "Type B - Foundational Gaps" rather than the code the
        // marker wrote. The buckets in ENGLISH_COPY hold the same labels for the cards.
        misconception_label: row.misconception_label,
        is_correct: row.is_correct,
        // What is behind the row, so it can say "6 of 8 parts" instead of looking like the
        // same row listed eight times.
        parts_total: row.parts_total,
        parts_correct: row.parts_correct,
        // English does not rank gaps. The field is required by the shared type but the tab
        // stopped reading it, so a constant here changes nothing on screen.
        priority: "medium" as const,
      })),
      priority_counts: {
        high: 0,
        medium: 0,
        low: 0,
        // The one count the summary strip actually shows. Computed server-side as the number
        // of distinct WRONG SKILLS that have prerequisites, because that is what the test
        // actually does: prereqRound never reads misconception_type. Counting Type B here
        // reported 1 while the Chain Analysis tab drew 7 chains from the same answers.
        chains_triggered: eng.counts.chains_triggered,
      },
      // The caption's denominator: the ROWS the table lists, after the parts of one question
      // are folded together. Not distinct_skills, which made this read "Showing 42 of 21", and
      // not the answer count, which would no longer match the rows on screen. The wire field
      // keeps the maths name; the noun comes from the copy context.
      total_skills_in_year: eng.counts.rows,
    }
  },

  getStudentPrereqGraph: async (
    req: {
      student_key: string
      strand_id?: number | null
      year_level_id?: number | null
      diagnostics_session_key?: string | null
    },
    signal?: AbortSignal,
  ) => {
    // Already the same shape: the English server returns clusters under `strand_*` precisely so
    // this needs no translation.
    const eng = await getEngStudentPrereqGraph(req, signal)
    return {
      strands: eng.strands,
      nodes: eng.nodes,
      edges: eng.edges,
      summary: eng.summary,
    }
  },
}
